import { FilterCategory, FilterFieldMeta } from '@/types/filterBuilder';

// All patient filter field metadata
export const patientFilterFields: FilterFieldMeta[] = [
  // Patient Demographics
  {
    key: 'patient_name',
    label: 'Patient Name',
    category: 'demographics',
    inputType: 'text',
    placeholder: 'Search by name...',
  },
  {
    key: 'patient_identifier',
    label: 'MRN / FHIR ID',
    category: 'demographics',
    inputType: 'text',
    placeholder: 'MRN, external id or FHIR id',
    description: 'Case-insensitive partial match on MRN / external id or FHIR id',
  },
  {
    key: 'patient_phone',
    label: 'Phone Number',
    category: 'demographics',
    inputType: 'text',
    placeholder: 'e.g., 555-123-4567',
  },
  {
    key: 'patient_gender',
    label: 'Gender',
    category: 'demographics',
    inputType: 'select',
    options: [
      { value: 'M', label: 'Male' },
      { value: 'F', label: 'Female' },
    ],
  },
  {
    key: 'patient_age',
    label: 'Age Range',
    category: 'demographics',
    inputType: 'number-range',
    rangeKeys: { start: 'patient_age_min', end: 'patient_age_max' },
    placeholder: 'Age',
  },
  {
    key: 'include_tags',
    label: 'Include Tags',
    category: 'demographics',
    inputType: 'text',
    placeholder: 'tag1, tag2, ...',
    description: 'Comma-separated tags to include',
  },
  {
    key: 'exclude_tags',
    label: 'Exclude Tags',
    category: 'demographics',
    inputType: 'text',
    placeholder: 'tag1, tag2, ...',
    description: 'Comma-separated tags to exclude',
  },

  // Call History
  {
    key: 'call_statuses',
    label: 'Call Status',
    category: 'call-history',
    inputType: 'multi-select',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'completed', label: 'Completed' },
      { value: 'failed', label: 'Failed' },
      { value: 'voicemail', label: 'Voicemail' },
    ],
  },
  {
    key: 'call_types',
    label: 'Call Type',
    category: 'call-history',
    inputType: 'multi-select',
    options: [
      { value: 'consent', label: 'Consent' },
      { value: 'check_in', label: 'Check-in' },
    ],
  },
  {
    key: 'call_date',
    label: 'Call Date Range',
    category: 'call-history',
    inputType: 'date-range',
    rangeKeys: { start: 'call_date_start', end: 'call_date_end' },
  },
  {
    key: 'has_calls',
    label: 'Has Calls',
    category: 'call-history',
    inputType: 'boolean',
    description: 'Patient has at least one call',
  },
  {
    key: 'never_answered',
    label: 'Never Answered',
    category: 'call-history',
    inputType: 'boolean',
    description: 'Patient has never completed a call',
  },
  {
    key: 'latest_call_status',
    label: 'Latest Call Status',
    category: 'call-history',
    inputType: 'multi-select',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'completed', label: 'Completed' },
      { value: 'failed', label: 'Failed' },
      { value: 'voicemail', label: 'Voicemail' },
    ],
  },
  {
    key: 'call_attempts',
    label: 'Call Attempts',
    category: 'call-history',
    inputType: 'number-range',
    rangeKeys: { start: 'call_attempts_min', end: 'call_attempts_max' },
  },

  // Consent Management
  {
    key: 'consent_status',
    label: 'Consent Status',
    category: 'consent',
    inputType: 'multi-select',
    options: [
      { value: 'CONSENTED', label: 'Consented' },
      { value: 'DECLINED', label: 'Declined' },
      { value: 'UNCERTAIN', label: 'Uncertain' },
      { value: 'NOT_ASKED', label: 'Not Asked' },
      { value: 'none', label: 'No Consent Record' },
    ],
  },
  {
    key: 'consent_method',
    label: 'Consent Method',
    category: 'consent',
    inputType: 'multi-select',
    options: [
      { value: 'phone', label: 'Phone' },
      { value: 'in_person', label: 'In Person' },
      { value: 'online', label: 'Online' },
    ],
  },
  {
    key: 'needs_consent',
    label: 'Needs Consent',
    category: 'consent',
    inputType: 'boolean',
    description: 'Patient requires consent',
  },

  // Time-based Filters
  {
    key: 'last_contact',
    label: 'Last Contact Date',
    category: 'time-based',
    inputType: 'date-range',
    rangeKeys: { start: 'last_contact_start', end: 'last_contact_end' },
  },
  {
    key: 'due_for_contact',
    label: 'Due for Contact',
    category: 'time-based',
    inputType: 'boolean',
    description: 'Patient is due for a follow-up',
  },
  {
    key: 'overdue_contact',
    label: 'Overdue Contact',
    category: 'time-based',
    inputType: 'boolean',
    description: 'Patient is overdue for contact',
  },

  // Care Management
  {
    key: 'care_plan_status',
    label: 'Care Plan Status',
    category: 'care-management',
    inputType: 'multi-select',
    options: [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active' },
      { value: 'approved', label: 'Approved' },
      { value: 'draft', label: 'Draft' },
      { value: 'pending', label: 'Pending' },
      { value: 'declined', label: 'Declined' },
    ],
  },
  {
    key: 'has_active_care_plan',
    label: 'Has Active Care Plan',
    category: 'care-management',
    inputType: 'boolean',
  },
  {
    key: 'scheduled_calls_pending',
    label: 'Scheduled Calls Pending',
    category: 'care-management',
    inputType: 'boolean',
    description: 'Patient has pending scheduled calls',
  },

  // Advanced Filters
  {
    key: 'call_success_rate',
    label: 'Call Success Rate',
    category: 'advanced',
    inputType: 'number-range',
    rangeKeys: { start: 'call_success_rate_min', end: 'call_success_rate_max' },
    description: 'Range 0.0 to 1.0',
  },
  {
    key: 'response_pattern',
    label: 'Response Pattern',
    category: 'advanced',
    inputType: 'select',
    options: [
      { value: 'consistent', label: 'Consistent' },
      { value: 'intermittent', label: 'Intermittent' },
      { value: 'non_responsive', label: 'Non-responsive' },
    ],
  },
  {
    key: 'priority_level',
    label: 'Priority Level',
    category: 'advanced',
    inputType: 'select',
    options: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ],
  },
  {
    key: 'scheduled_today',
    label: 'Scheduled Today',
    category: 'advanced',
    inputType: 'boolean',
  },
  {
    key: 'scheduled_tomorrow',
    label: 'Scheduled Tomorrow',
    category: 'advanced',
    inputType: 'boolean',
  },
  {
    key: 'scheduled_this_week',
    label: 'Scheduled This Week',
    category: 'advanced',
    inputType: 'boolean',
  },
];

// Organized by category
export const patientFilterCategories: FilterCategory[] = [
  {
    id: 'demographics',
    title: 'Patient Demographics',
    filters: patientFilterFields.filter((f) => f.category === 'demographics'),
  },
  {
    id: 'call-history',
    title: 'Call History',
    filters: patientFilterFields.filter((f) => f.category === 'call-history'),
  },
  {
    id: 'consent',
    title: 'Consent Management',
    filters: patientFilterFields.filter((f) => f.category === 'consent'),
  },
  {
    id: 'time-based',
    title: 'Time-based',
    filters: patientFilterFields.filter((f) => f.category === 'time-based'),
  },
  {
    id: 'care-management',
    title: 'Care Management',
    filters: patientFilterFields.filter((f) => f.category === 'care-management'),
  },
  {
    id: 'advanced',
    title: 'Advanced',
    filters: patientFilterFields.filter((f) => f.category === 'advanced'),
  },
];

// Helper to get filter field by key
export function getPatientFilterByKey(key: string): FilterFieldMeta | undefined {
  return patientFilterFields.find((f) => f.key === key);
}
